import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBaseUrl } from './utils/apiConfig';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import RegisterTeacher from './pages/RegisterTeacher';
import RegisterStudent from './pages/RegisterStudent';
import RegisterOrg from './pages/RegisterOrg';
import Dashboard from './pages/Dashboard';
import KBHCurriculum from './pages/KBHCurriculum';
import NotFound from './pages/NotFound';
import UserDashboard from './pages/UserDashboard';
import TeacherDashboard from './pages/TeacherDashboard';
import StudentDashboard from './pages/StudentDashboard';
import AdminDashboard from './pages/AdminDashboard';
import OrgAdminDashboard from './pages/OrgAdminDashboard';
import Resources from './pages/Resources';
import Subscriptions from './pages/Subscriptions';
import Contact from './pages/Contact';
import News from './pages/News';
import NewsArticle from './pages/NewsArticle';
import NewsAdmin from './pages/NewsAdmin';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentUpdate from './pages/PaymentUpdate';
import Basket from './pages/Basket';
import EditAsset from './pages/EditAsset';
import RequestPasswordReset from './pages/RequestPasswordReset';
import ResetPassword from './pages/ResetPassword';
import EditPlan from './pages/EditPlan';
import Profile from './pages/Profile';
import User from './pages/User';
import Organisation from './pages/Organisation';
import Logout from './pages/Logout';
import SchoolAdminDashboard from './pages/SchoolAdminDashboard';
import StudentDetail from './pages/StudentDetail';
import SchoolAdminDetail from './pages/SchoolAdminDetail';
import './styles/global.css';

// Configure axios to include credentials and base URL
axios.defaults.withCredentials = true;

// Set up global axios interceptor for automatic base URL handling
const apiBaseUrl = getApiBaseUrl();
axios.defaults.baseURL = apiBaseUrl;

// Add request interceptor to log API calls (for debugging)
axios.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// On 401 "Logged in elsewhere", redirect to login so user can sign in again
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const msg = error.response?.data?.message || error.response?.data?.error || '';
    if (error.response?.status === 401 && typeof msg === 'string' && msg.toLowerCase().includes('another device')) {
      window.location.href = '/login?reason=logged_in_elsewhere';
    }
    return Promise.reject(error);
  }
);


// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Public Route component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return children;
  }
  
  // Check for impersonation first
  if (user && user.isImpersonated) {
    if (user.userType === 'organization' && user.isOrgAdmin) {
      return <Navigate to="/org-admin-dashboard"/>;
    } else if (user.userType === 'schoolAdmin' && user.isSchAdmin) {
      return <Navigate to="/school-admin-dashboard" />;
    } else if (user.userType === 'student' || user.role === 'student') {
      return <Navigate to="/student-dashboard" />;
    } else {
      return <Navigate to="/teacher-dashboard" />;
    }
  } else if (user && user.isAdmin) {
    return <Navigate to="/admin-dashboard" />;
  } else if (user && user.isOrgAdmin) {
    return <Navigate to="/org-admin-dashboard"/>;
  } else if (user && user.isSchAdmin) {
    // School admin: always go to dashboard regardless of orgCode or subscription status
    return <Navigate to="/school-admin-dashboard" />;
  } else if (user && (user.userType === 'student' || user.role === 'student')) {
    // Students go to student dashboard
    return <Navigate to="/student-dashboard" />;
  } else {
    // All other regular users are teachers
    return <Navigate to="/teacher-dashboard" />;
  }
};

// Subscription Check Route component
const SubscriptionCheckRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  // Note: Impersonation routing is handled in PublicRoute component
  // This component only handles subscription checks for already-routed users

  // If user is admin or org admin, allow access (they don't need subscriptions)
  if (user && (user.isAdmin || user.isOrgAdmin || user.userType === 'organization')) {
    return children;
  }

  // All teachers are part of a school (require school code) - allow access
  if (user && (user.role === 'teacher' || user.userType === 'teacher')) {
    return children;
  }

  // Students should not be here - they have their own route
  if (user && (user.userType === 'student' || user.role === 'student')) {
    return <Navigate to="/student-dashboard" />;
  }

  // For school admins, check for subscriptions
  // But if they're in teacher role, allow access to teacher dashboard without subscription check
  if (user?.isSchAdmin && user?.currentActiveRole === 'teacher') {
    return children; // Allow school admins in teacher role to access teacher dashboard
  }
  
  // Additional check: if user is a school admin accessing teacher dashboard, allow access
  // This handles cases where the user object might not be immediately updated after role switch
  if (user?.isSchAdmin && window.location.pathname === '/teacher-dashboard') {
    return children; // Allow school admins to access teacher dashboard regardless of currentActiveRole
  }
  
  const hasAnySubscription = user?.subscriptions && user.subscriptions.length > 0;

  if (!hasAnySubscription) {
    return <Navigate to="/subscriptions" />;
  }

  return children;
};

// School Admin Route component
const SchoolAdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }
  
  // Check if user is a student and redirect them
  if (user && (user.userType === 'student' || user.role === 'student' || user.studentCode)) {
    return <Navigate to="/student-dashboard" />;
  }
  
  // Only allow if user is a school admin (not org admin, not teacher, not student)
  if (user && user.isSchAdmin) {
    // School admin - always allow access regardless of orgCode or subscription status
    return children;
  }
  
  // Otherwise, redirect to a safe default (dashboard)
  return <Navigate to="/dashboard" />;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes with Layout */}
      <Route path="/" element={
        <Layout fullWidth={true}>
          <Home />
        </Layout>
      } />
      <Route path="/contact" element={
        <Layout fullWidth={true}>
          <Contact />
        </Layout>
      } />
      <Route path="/kbh-curriculum" element={
        <Layout fullWidth={true}>
          <KBHCurriculum />
        </Layout>
      } />
      <Route path="/news" element={
        <Layout fullWidth={true}>
          <News />
        </Layout>
      } />
      <Route path="/news/:id" element={
        <Layout fullWidth={true}>
          <NewsArticle />
        </Layout>
      } />

      {/* Auth Routes with Layout */}
      <Route path="/login" element={
        <PublicRoute>
          <Layout fullWidth={true}>
            <Login />
          </Layout>
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <Layout fullWidth={true}>
            <Register />
          </Layout>
        </PublicRoute>
      } />
      <Route path="/register/teacher" element={
        <PublicRoute>
          <Layout fullWidth={true}>
            <RegisterTeacher />
          </Layout>
        </PublicRoute>
      } />
      <Route path="/register/student" element={
        <PublicRoute>
          <Layout fullWidth={true}>
            <RegisterStudent />
          </Layout>
        </PublicRoute>
      } />
      <Route path="/register-org" element={
        <PublicRoute>
          <Layout fullWidth={true}>
            <RegisterOrg />
          </Layout>
        </PublicRoute>
      } />
      <Route path="/request-password-reset" element={
        <PublicRoute>
          <Layout>
            <RequestPasswordReset />
          </Layout>
        </PublicRoute>
      } />
      {/* Token in path (email links) or query (?token=) — both must resolve */}
      <Route path="/reset-password/:token" element={
        <PublicRoute>
          <Layout>
            <ResetPassword />
          </Layout>
        </PublicRoute>
      } />
      <Route path="/reset-password" element={
        <PublicRoute>
          <Layout>
            <ResetPassword />
          </Layout>
        </PublicRoute>
      } />
      <Route path="/logout" element={<Logout />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <SubscriptionCheckRoute>
          <Dashboard />
        </SubscriptionCheckRoute>
      } />
      <Route path="/user-dashboard" element={
        <SubscriptionCheckRoute>
          <UserDashboard />
        </SubscriptionCheckRoute>
      } />
      <Route path="/teacher-dashboard" element={
        <SubscriptionCheckRoute>
          <TeacherDashboard />
        </SubscriptionCheckRoute>
      } />
      <Route path="/student-dashboard" element={
        <ProtectedRoute>
          <StudentDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin-dashboard" element={<AdminDashboard />} />
      <Route path="/news-admin" element={
        <ProtectedRoute>
          <NewsAdmin />
        </ProtectedRoute>
      } />
      <Route path="/org-admin-dashboard" element={
        <ProtectedRoute>
          <OrgAdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/resources" element={
        <Layout>
          <Resources />
        </Layout>
      } />
      <Route path="/subscriptions" element={<ProtectedRoute><Subscriptions /></ProtectedRoute>} />
      <Route path="/payment" element={<Payment />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-update" element={
        <ProtectedRoute>
          <PaymentUpdate />
        </ProtectedRoute>
      } />
      <Route path="/basket" element={
        <ProtectedRoute>
          <Basket />
        </ProtectedRoute>
      } />
      <Route path="/edit-asset/:id" element={
        <ProtectedRoute>
          <EditAsset />
        </ProtectedRoute>
      } />
      <Route path="/edit-plan/:id" element={<EditPlan />} />
      <Route path="/profile" element={
        <SubscriptionCheckRoute>
          <Profile />
        </SubscriptionCheckRoute>
      } />
      <Route path="/user/:id" element={
        <ProtectedRoute>
          <User />
        </ProtectedRoute>
      } />
      <Route path="/organisation/:id" element={
        <ProtectedRoute>
          <Organisation />
        </ProtectedRoute>
      } />
      <Route path="/school-admin-dashboard" element={
        <SchoolAdminRoute>
          <SchoolAdminDashboard />
        </SchoolAdminRoute>
      } />
      <Route path="/student/:id" element={
        <ProtectedRoute>
          <StudentDetail />
        </ProtectedRoute>
      } />
      <Route path="/school-admin/:id" element={
        <ProtectedRoute>
          <SchoolAdminDetail />
        </ProtectedRoute>
      } />

      {/* 404 Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;
